/**
 * Step 2 — Escolha do Plano.
 *
 * Réplica do frame `web-step3-plano` (o nome do frame guarda a numeração antiga;
 * a v6 §10 traz o plano para antes dos dependentes): os planos ficam lado a lado
 * em cards, cada um com o total mensal em destaque e o valor só do titular
 * abaixo. O card selecionado ganha borda da marca, fundo tingido e o check no
 * canto — o design não usa radio, o card inteiro é o alvo do clique.
 *
 * A comparação "Com N dependentes" × "Sem dependentes" fica UMA vez abaixo dos
 * cards, referente ao plano selecionado, e não repetida dentro de cada card.
 *
 * O recálculo é client-side no MVP (spec §10.1). Se algum preço vier fora do
 * contrato (não inteiro, negativo), `pricing.ts` lança e o card exibe preço
 * indisponível em vez de "R$ NaN" — e o plano não fica selecionável.
 */

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Alert } from "@mui/material";
import styled from "styled-components";

import { StatusChip } from "../components/StatusChip";
import { StepActions } from "../components/StepActions";
import { useExtensionForm } from "../context";
import { formatAccommodation } from "../helpers";
import { calculateMonthlyTotalCents, formatCents } from "../pricing";
import {
  Caption,
  Divider,
  Header,
  Paragraph,
  Row,
  SectionLabel,
  Stack,
  TwoColumns,
} from "../styled";
import { COLORS, MOBILE_MEDIA_QUERY } from "../tokens";
import { IEligiblePlan } from "../types";

const PLAN_UNAVAILABLE_PRICE = "Preço indisponível";

/** Os três cards ficam lado a lado no web e empilham no mobile. */
const PlanGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  ${MOBILE_MEDIA_QUERY} {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.button<{ $selected: boolean }>`
  position: relative;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font: inherit;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border-radius: 16px;
  border: solid ${({ $selected }) => ($selected ? "2px" : "1px")}
    ${({ $selected }) => ($selected ? COLORS.brand : COLORS.border)};
  background-color: ${({ $selected }) =>
    $selected ? COLORS.brandTint : COLORS.surface};
  transition: border-color 0.2s, background-color 0.2s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SelectedCheck = styled(CheckCircleIcon)`
  position: absolute;
  top: 12px;
  right: 12px;
  color: ${COLORS.brand};
`;

const PlanPrice = styled.strong`
  color: ${COLORS.ink};
  font-size: 24px;
  font-weight: 700;
  line-height: 130%;
`;

/** Quadros cinza da comparação, abaixo dos cards. */
const CompareBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 16px;
  border-radius: 12px;
  background-color: ${COLORS.neutralSoft};
`;

const CompareValue = styled.strong<{ $muted?: boolean }>`
  color: ${({ $muted }) => ($muted ? COLORS.inkMuted : COLORS.ink)};
  font-size: 18px;
  font-weight: 600;
  line-height: 130%;
`;

/**
 * Formata um total protegendo a UI do `PricingError`. Devolve `null` quando o
 * preço não pode ser calculado, para o chamador decidir o que exibir.
 */
function formatTotalOrNull(
  plan: IEligiblePlan,
  retainedCount: number
): string | null {
  try {
    return formatCents(calculateMonthlyTotalCents(plan, retainedCount));
  } catch (error) {
    return null;
  }
}

export function Step2Plan() {
  const { data, state, updateState, goNext, goBack } = useExtensionForm();
  const { eligiblePlans } = data;

  const retainedCount = state.retainedDependentTaxPayerIds.length;
  const hasRetainedDependents = retainedCount > 0;

  const selectedPlan = eligiblePlans.find(
    (plan) => plan.id === state.chosenPlanId
  );

  const selectedWithDependents = selectedPlan
    ? formatTotalOrNull(selectedPlan, retainedCount)
    : null;
  const selectedHolderOnly = selectedPlan
    ? formatTotalOrNull(selectedPlan, 0)
    : null;

  const dependentsLabel = `Com ${retainedCount} ${
    retainedCount === 1 ? "dependente" : "dependentes"
  }`;

  return (
    <Stack $gap="24px">
      <Stack $gap="8px">
        <Row $gap="12px">
          <Header>Escolha do Plano</Header>
          {selectedPlan?.isCurrentPlan && (
            <StatusChip tone="infoSolid">Seu plano atual</StatusChip>
          )}
        </Row>
        <Paragraph>
          Seu plano atual está pré-selecionado. Você pode mantê-lo ou escolher
          outra opção disponível com base na rede de atendimento desejada.
        </Paragraph>
      </Stack>

      <PlanGrid>
        {eligiblePlans.map((plan) => {
          const withDependents = formatTotalOrNull(plan, retainedCount);
          const holderOnly = formatTotalOrNull(plan, 0);
          const isSelectable = withDependents !== null;
          const isSelected = plan.id === state.chosenPlanId;

          return (
            <PlanCard
              key={plan.id}
              type="button"
              $selected={isSelected}
              disabled={!isSelectable}
              aria-pressed={isSelected}
              onClick={() => updateState({ chosenPlanId: plan.id })}
            >
              {isSelected && <SelectedCheck />}

              <StatusChip tone="infoSolid">
                {plan.isCurrentPlan ? "Seu plano atual" : "Disponível"}
              </StatusChip>

              <Stack $gap="2px">
                <SectionLabel>{plan.name}</SectionLabel>
                <Caption>{formatAccommodation(plan.accommodation)}</Caption>
              </Stack>

              <Stack $gap="2px">
                <PlanPrice>
                  {withDependents
                    ? `${withDependents}/mês`
                    : PLAN_UNAVAILABLE_PRICE}
                </PlanPrice>
                {!!holderOnly && <Caption>{holderOnly}/mês só titular</Caption>}
              </Stack>
            </PlanCard>
          );
        })}
      </PlanGrid>

      {eligiblePlans.length === 0 && (
        <Alert severity="warning">
          Nenhum plano elegível foi encontrado para esta oferta. Entre em contato
          com o RH da {data.offer.companyName}.
        </Alert>
      )}

      {!!selectedPlan && (
        <>
          <Divider />

          <TwoColumns $keepOnMobile>
            <CompareBox>
              <Caption>
                {hasRetainedDependents ? dependentsLabel : "Total mensal"}
              </Caption>
              <CompareValue>
                {selectedWithDependents ?? PLAN_UNAVAILABLE_PRICE}
              </CompareValue>
              <Caption>/mês</Caption>
            </CompareBox>

            <CompareBox>
              <Caption>Sem dependentes</Caption>
              <CompareValue $muted={hasRetainedDependents}>
                {selectedHolderOnly ?? PLAN_UNAVAILABLE_PRICE}
              </CompareValue>
              <Caption>/mês</Caption>
            </CompareBox>
          </TwoColumns>
        </>
      )}

      <Alert severity="info">
        O pagamento será realizado via desconto em folha ou boleto bancário
        direto, conforme política da sua empresa.
      </Alert>

      <Alert severity="info">
        Lembre-se: os valores apresentados consideram os dependentes que serão
        mantidos. Você define essa lista no próximo passo, e o total é
        recalculado automaticamente.
      </Alert>

      <StepActions
        nextLabel="Próximo: Dependentes →"
        onNext={goNext}
        onBack={goBack}
        isNextDisabled={!selectedPlan || !selectedWithDependents}
      />
    </Stack>
  );
}
